import { useForm } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface Sample {
  id: number
  name: string
}

interface Cupping {
  id: number
  title: string
  cupping_form: string
  cupping_form_label: string
  samples: Sample[]
}

interface ExistingCupping {
  sample_id: number
  fragrance_score: number
  aroma_rating: number
  flavor_score: number
  aftertaste_score: number
  acidity_score: number
  sweetness_score: number
  body_score: number
  overall_score: number
  non_uniform_cups: number[]
  defective_cups_list: number[]
  defect_type: string
  cva_section_notes: Record<string, string>
  roast_level: string
  notes: string
}

interface Props {
  cupping: Cupping
  existing_cupping?: ExistingCupping | null
}

const CVA_SECTIONS = [
  { key: "fragrance", label: "Fragancia", dataField: "fragrance_score" },
  { key: "aroma", label: "Aroma", dataField: "aroma_rating" },
  { key: "flavor", label: "Sabor", dataField: "flavor_score" },
  { key: "aftertaste", label: "Retrogusto", dataField: "aftertaste_score" },
  { key: "acidity", label: "Acidez", dataField: "acidity_score" },
  { key: "sweetness", label: "Dulzura", dataField: "sweetness_score" },
  {
    key: "mouthfeel",
    label: "Cuerpo / Sensación en boca",
    dataField: "body_score",
  },
  { key: "overall", label: "General", dataField: "overall_score" },
] as const

const QUALITY_LABELS: Record<number, string> = {
  1: "Extremadamente baja",
  2: "Muy baja",
  3: "Moderadamente baja",
  4: "Ligeramente baja",
  5: "Ni alta ni baja",
  6: "Ligeramente alta",
  7: "Moderadamente alta",
  8: "Muy alta",
  9: "Extremadamente alta",
}

const DEFECT_TYPES = [
  { value: "moldy", label: "Mohoso" },
  { value: "phenolic", label: "Fenólico" },
  { value: "potato", label: "Papa" },
]

// CVA Score formula: S = 0.65625 * Σhi + 52.75 - 2u - 4d
function calculateCvaScore(
  sectionScores: number[],
  nonUniformCount: number,
  defectiveCount: number,
): number {
  const sum = sectionScores.reduce((a, b) => a + b, 0)
  const raw = 0.65625 * sum + 52.75 - 2 * nonUniformCount - 4 * defectiveCount
  return Math.round(raw * 4) / 4 // Round to nearest 0.25
}

// Two-way table lookup (from the PDF)
function lookupCvaScore(sumOfScores: number): number {
  const table: Record<number, number> = {
    8: 58.0,
    9: 58.75,
    10: 59.25,
    11: 60.0,
    12: 60.75,
    13: 61.25,
    14: 62.0,
    15: 62.5,
    16: 63.25,
    17: 64.0,
    18: 64.5,
    19: 65.25,
    20: 66.0,
    21: 66.5,
    22: 67.25,
    23: 67.75,
    24: 68.5,
    25: 69.25,
    26: 69.75,
    27: 70.5,
    28: 71.25,
    29: 71.75,
    30: 72.5,
    31: 73.0,
    32: 73.75,
    33: 74.5,
    34: 75.0,
    35: 75.75,
    36: 76.5,
    37: 77.0,
    38: 77.75,
    39: 78.25,
    40: 79.0,
    41: 79.75,
    42: 80.25,
    43: 81.0,
    44: 81.75,
    45: 82.25,
    46: 83.0,
    47: 83.5,
    48: 84.25,
    49: 85.0,
    50: 85.5,
    51: 86.25,
    52: 87.0,
    53: 87.5,
    54: 88.25,
    55: 88.75,
    56: 89.5,
    57: 90.25,
    58: 90.75,
    59: 91.5,
    60: 92.25,
    61: 92.75,
    62: 93.5,
    63: 94.0,
    64: 94.75,
    65: 95.5,
    66: 96.0,
    67: 96.75,
    68: 97.5,
    69: 98.0,
    70: 98.75,
    71: 99.25,
    72: 100.0,
  }
  return table[sumOfScores] ?? calculateCvaScore([sumOfScores], 0, 0)
}

export default function CvaAffectiveForm({ cupping, existing_cupping }: Props) {
  const [currentSample, setCurrentSample] = useState(0)
  const [showSummaryModal, setShowSummaryModal] = useState(false)

  const { data, setData, post, processing } = useForm({
    sample_id: existing_cupping?.sample_id || cupping.samples[0]?.id,
    fragrance_score: existing_cupping?.fragrance_score || 5,
    aroma_rating: existing_cupping?.aroma_rating || 5,
    flavor_score: existing_cupping?.flavor_score || 5,
    aftertaste_score: existing_cupping?.aftertaste_score || 5,
    acidity_score: existing_cupping?.acidity_score || 5,
    sweetness_score: existing_cupping?.sweetness_score || 5,
    body_score: existing_cupping?.body_score || 5,
    overall_score: existing_cupping?.overall_score || 5,
    non_uniform_cups: (existing_cupping?.non_uniform_cups || []) as number[],
    defective_cups_list: (existing_cupping?.defective_cups_list ||
      []) as number[],
    defect_type: existing_cupping?.defect_type || "",
    cva_section_notes: (existing_cupping?.cva_section_notes || {}) as Record<
      string,
      string
    >,
    roast_level: existing_cupping?.roast_level || "medium",
    notes: existing_cupping?.notes || "",
    // Fields needed by backend but unused in CVA
    total_cups: 5,
    fragrance_dry: 0,
    fragrance_break: 0,
    acidity_intensity: 0,
    body_level: 0,
    balance_score: 0,
    uniformity_score: 0,
    clean_cup_score: 0,
    defects_cups: 0,
    defects_intensity: 0,
    re_roast: "no",
  })

  const getSectionScore = (section: (typeof CVA_SECTIONS)[number]): number => {
    return data[section.dataField as keyof typeof data] as number
  }

  const setSectionScore = (
    section: (typeof CVA_SECTIONS)[number],
    value: number,
  ) => {
    setData(section.dataField as keyof typeof data, value as never)
  }

  const getSectionNote = (sectionKey: string): string => {
    return data.cva_section_notes[sectionKey] || ""
  }

  const setSectionNote = (sectionKey: string, value: string) => {
    setData("cva_section_notes", {
      ...data.cva_section_notes,
      [sectionKey]: value,
    })
  }

  const toggleCup = (
    field: "non_uniform_cups" | "defective_cups_list",
    cupNumber: number,
  ) => {
    const current = data[field]
    if (current.includes(cupNumber)) {
      setData(
        field,
        current.filter((c) => c !== cupNumber),
      )
    } else {
      setData(field, [...current, cupNumber])
    }
  }

  const allSectionScores = CVA_SECTIONS.map((s) => getSectionScore(s))
  const sumOfScores = allSectionScores.reduce((a, b) => a + b, 0)
  const baseScore = lookupCvaScore(sumOfScores)
  const nonUniformDeduction = data.non_uniform_cups.length * 2
  const defectiveDeduction = data.defective_cups_list.length * 4
  const totalScore = Math.max(
    0,
    baseScore - nonUniformDeduction - defectiveDeduction,
  )

  const handleSampleChange = (index: number) => {
    setCurrentSample(index)
    setData("sample_id", cupping.samples[index]?.id)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setShowSummaryModal(true)
  }

  const handleConfirmSubmit = () => {
    if (!cupping.samples || cupping.samples.length === 0) {
      alert("Error: Esta sesión de catación no tiene muestras asociadas.")
      return
    }
    const sampleId = data.sample_id || cupping.samples[currentSample]?.id
    if (!sampleId) {
      alert("Error: No se pudo identificar la muestra.")
      return
    }
    if (data.sample_id !== sampleId) {
      setData("sample_id", sampleId)
    }
    post(`/cuppings/${cupping.id}/cup`)
  }

  const sample = cupping.samples[currentSample]

  return (
    <>
      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sample Selector */}
            <div className="flex items-center gap-4 border-b pb-4">
              <div className="flex items-center gap-2">
                {cupping.samples.map((s: Sample, idx: number) => (
                  <Button
                    key={s.id}
                    type="button"
                    variant={currentSample === idx ? "default" : "outline"}
                    onClick={() => handleSampleChange(idx)}
                    className={currentSample === idx ? "bg-primary" : ""}
                  >
                    {idx + 1}
                  </Button>
                ))}
              </div>
              <div className="flex-1" />
              <div className="text-right">
                <p className="text-muted-foreground text-sm">
                  {sample?.name || `Muestra #${currentSample + 1}`}
                </p>
              </div>
            </div>

            {/* Score Summary */}
            <div className="bg-primary/5 flex items-center justify-between rounded-lg p-4">
              <div>
                <p className="text-sm font-medium">Puntuación CVA</p>
                <p className="text-muted-foreground text-xs">
                  Suma de secciones: {sumOfScores} / 72
                </p>
              </div>
              <span className="text-primary text-3xl font-bold">
                {totalScore.toFixed(2)}
              </span>
            </div>

            {/* Impression of Quality Legend */}
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 text-sm font-semibold tracking-wide uppercase">
                Impresion de Calidad
              </h3>
              <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-xs">
                {Object.entries(QUALITY_LABELS).map(([num, label]) => (
                  <div key={num} className="flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold">
                      {num}
                    </span>
                    <span className="text-muted-foreground">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 8 Affective Sections */}
            <div className="space-y-4">
              {CVA_SECTIONS.map((section) => {
                const score = getSectionScore(section)
                return (
                  <Card key={section.key}>
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{section.label}</h3>
                        <span className="text-muted-foreground text-sm">
                          Puntuacion: {score}
                        </span>
                      </div>

                      {/* 9-point bubble selector */}
                      <div className="flex items-center gap-1.5">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((value) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setSectionScore(section, value)}
                            className={`flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition-all ${
                              value === score
                                ? "bg-primary border-primary text-primary-foreground scale-110"
                                : value < score
                                  ? "bg-primary/20 border-primary/40 text-primary"
                                  : "border-muted-foreground/30 text-muted-foreground hover:border-primary/50 hover:bg-primary/5"
                            }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>

                      {/* Quality label */}
                      <p className="text-muted-foreground text-xs">
                        {QUALITY_LABELS[score]}
                      </p>

                      {/* Notes */}
                      <Textarea
                        value={getSectionNote(section.key)}
                        onChange={(e) =>
                          setSectionNote(section.key, e.target.value)
                        }
                        placeholder="Notas..."
                        rows={2}
                        className="resize-none text-sm"
                      />
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            {/* Uniformity & Defects */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Non-uniform cups */}
              <Card>
                <CardContent className="space-y-3 p-4">
                  <h3 className="font-semibold">Tazas no uniformes</h3>
                  <p className="text-muted-foreground text-xs">
                    Marca las tazas con diferencias cualitativas (-2 pts c/u)
                  </p>
                  <div className="flex items-center gap-3">
                    {[1, 2, 3, 4, 5].map((cup) => (
                      <label
                        key={cup}
                        className="flex flex-col items-center gap-1"
                      >
                        <Checkbox
                          checked={data.non_uniform_cups.includes(cup)}
                          onCheckedChange={() =>
                            toggleCup("non_uniform_cups", cup)
                          }
                        />
                        <span className="text-muted-foreground text-xs">
                          #{cup}
                        </span>
                      </label>
                    ))}
                  </div>
                  {data.non_uniform_cups.length > 0 && (
                    <p className="text-destructive text-xs font-medium">
                      Deduccion: -{nonUniformDeduction} pts
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Defective cups */}
              <Card>
                <CardContent className="space-y-3 p-4">
                  <h3 className="font-semibold">Tazas defectuosas</h3>
                  <p className="text-muted-foreground text-xs">
                    Marca las tazas con defectos sensoriales (-4 pts c/u)
                  </p>
                  <div className="flex items-center gap-3">
                    {[1, 2, 3, 4, 5].map((cup) => (
                      <label
                        key={cup}
                        className="flex flex-col items-center gap-1"
                      >
                        <Checkbox
                          checked={data.defective_cups_list.includes(cup)}
                          onCheckedChange={() =>
                            toggleCup("defective_cups_list", cup)
                          }
                        />
                        <span className="text-muted-foreground text-xs">
                          #{cup}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Defect type */}
                  {data.defective_cups_list.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm">Tipo de defecto</Label>
                      <div className="flex items-center gap-4">
                        {DEFECT_TYPES.map((defect) => (
                          <label
                            key={defect.value}
                            className="flex cursor-pointer items-center gap-2"
                          >
                            <input
                              type="radio"
                              name="defect_type"
                              value={defect.value}
                              checked={data.defect_type === defect.value}
                              onChange={(e) =>
                                setData("defect_type", e.target.value)
                              }
                              className="text-primary"
                            />
                            <span className="text-sm">{defect.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-destructive text-xs font-medium">
                        Deduccion: -{defectiveDeduction} pts
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* General Notes */}
            <Card>
              <CardContent className="space-y-3 p-4">
                <h3 className="font-semibold">Notas generales</h3>
                <Textarea
                  value={data.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setData("notes", e.target.value)
                  }
                  placeholder="Notas adicionales sobre la muestra..."
                  rows={4}
                  className="resize-none"
                />
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={processing}
                size="lg"
                className="bg-primary hover:bg-primary/90"
              >
                Revisar y Enviar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Summary Modal */}
      <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              Resumen - Evaluacion Afectiva CVA
            </DialogTitle>
            <DialogDescription>
              Revisa tus puntuaciones antes de enviar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Sample Info */}
            <div className="bg-primary/10 rounded-lg p-4">
              <h3 className="mb-2 font-semibold">Informacion de Muestra</h3>
              <p className="text-sm">
                <span className="text-muted-foreground">Muestra:</span>{" "}
                <span className="font-medium">
                  {sample?.name || `Muestra #${currentSample + 1}`}
                </span>
              </p>
            </div>

            {/* Section Scores */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">
                Impresion de Calidad por Seccion
              </h4>
              <div className="space-y-1 text-sm">
                {CVA_SECTIONS.map((section) => (
                  <div key={section.key} className="flex justify-between">
                    <span>{section.label}</span>
                    <span className="font-semibold">
                      {getSectionScore(section)} / 9
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Deductions */}
            <div className="space-y-2 border-t pt-2">
              <h4 className="text-sm font-semibold">Deducciones</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>
                    Tazas no uniformes ({data.non_uniform_cups.length})
                  </span>
                  <span className="text-destructive font-semibold">
                    -{nonUniformDeduction}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>
                    Tazas defectuosas ({data.defective_cups_list.length})
                    {data.defect_type &&
                      ` - ${DEFECT_TYPES.find((d) => d.value === data.defect_type)?.label}`}
                  </span>
                  <span className="text-destructive font-semibold">
                    -{defectiveDeduction}
                  </span>
                </div>
              </div>
            </div>

            {/* Total Score */}
            <div className="bg-primary rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-lg font-semibold">
                    Puntuacion CVA Total
                  </span>
                  <p className="text-xs text-white/70">
                    S = 0.65625 x {sumOfScores} + 52.75 - {nonUniformDeduction}{" "}
                    - {defectiveDeduction}
                  </p>
                </div>
                <span className="text-3xl font-bold">
                  {totalScore.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSummaryModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmSubmit}
              disabled={processing}
              className="bg-primary hover:bg-primary/90"
            >
              {processing ? "Enviando..." : "Confirmar y Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
